import { inject, Injectable } from "@angular/core";
import {
    concat,
    defer,
    merge,
    Observable,
    Subject
} from "rxjs";
import {
    filter,
    last,
    map,
    shareReplay,
    switchMap,
    take
} from "rxjs/operators";
import { CommandService, ICommand } from "~app/common/services/cqs/command.service";
import { UpdateBetalningSkogsvardCommand } from "../commands/skogsvard-betalning/UpdateBetalningSkogsvardCommand";
import { KontraktSkogsvardBetalning } from "../models/skogsvard/skogsvard-models";
import { AvtalMediatorService } from "./avtal-mediator.service";
import { KontraktMediatorService } from "./kontrakt-mediator.service";
import { SkogsvardService } from "./skogsvard.service";

@Injectable()
export class SkogsvardBetalningMediatorService {
    readonly skogsvardBetalning$: Observable<KontraktSkogsvardBetalning[]>;
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly skogsvardService = inject(SkogsvardService);
    private readonly commandService = inject(CommandService);
    private readonly avtalMediatorService = inject(AvtalMediatorService);
    private readonly reloadBetalningarSrc = new Subject<number>();

    constructor() {
        const kontraktId$ = this.kontraktMediatorService.kontrakt$.pipe(
            filter(kontrakt => kontrakt.isNotSkogsvardFPVKontrakt),
            map(kontrakt => kontrakt.id)
        );

        this.skogsvardBetalning$ = merge(kontraktId$, this.reloadBetalningarSrc).pipe(
            switchMap(kontraktId => this.skogsvardService.getSkogsvardsBetalningarForKontrakt(kontraktId)),
            shareReplay(1)
        );
    }

    updateSkogsvardBetalning(kontraktGuid: string, betalning: KontraktSkogsvardBetalning): Observable<any> {
        const cmd = new UpdateBetalningSkogsvardCommand(kontraktGuid, betalning);

        const updateBetalningar$ = this.executeCommand(cmd);
        const loadBetalningar$ = this.loadbetalningarForKontrakt(betalning.virkeskopskontraktId);

        return concat(updateBetalningar$, loadBetalningar$)
            .pipe(
                last()
            );
    }

    loadbetalningarForKontrakt(kontraktId: number): Observable<KontraktSkogsvardBetalning[]> {
        return defer(() => {
            this.reloadBetalningarSrc.next(kontraktId);
            return this.skogsvardBetalning$.pipe(take(1));
        });
    }

    private executeCommand(cmd: ICommand): Observable<any> {
        this.commandService.addTransactionCommand(cmd);
        const obs = this.commandService.pushCommandQueueTransaction();

        obs.subscribe(x => {
            this.commandService.reset();
            this.avtalMediatorService.skogsvardBetalraderSaved.next();
        });

        return obs;
    }
}
